import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";
import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { WantedShiftSearch } from "./WantedShiftSearch";

type WantedAuthoringCardProps = {
  readonly selectedShiftId: string;
  readonly shifts: ShiftReturnType;
  readonly disabled: boolean;
  readonly highlightSelection?: boolean;
  readonly onShiftChange: (shiftId: string) => void;
  readonly onClearSelection: () => void;
};

export function WantedAuthoringCard({
  selectedShiftId,
  shifts,
  disabled,
  highlightSelection = false,
  onShiftChange,
  onClearSelection,
}: WantedAuthoringCardProps) {
  const selectedShift = shifts.find(
    (shift) => shift.shift_id === selectedShiftId
  );

  return (
    <aside
      aria-label="근무코드 선택"
      aria-disabled={disabled}
      className={cn(
        "bg-background p-4 transition-[background-color,box-shadow] duration-200 sm:p-5 xl:sticky xl:top-4 xl:flex xl:min-h-0 xl:flex-col xl:self-stretch xl:border-r xl:border-border/50 xl:[contain:size]",
        highlightSelection &&
          "animate-pulse bg-sky-50/70 ring-2 ring-inset ring-sig/50 motion-reduce:animate-none",
        disabled && "opacity-70"
      )}
    >
      {highlightSelection && (
        <p className="sr-only" role="status" aria-live="polite">
          근무코드를 먼저 선택해 주세요.
        </p>
      )}
      <div className="mb-4 shrink-0">
        <h2 className="text-base font-semibold text-text-main2">
          근무코드 선택
        </h2>
        <p className="mt-1 break-keep text-xs leading-5 text-text-sub">
          {selectedShiftId
            ? "선택한 코드를 다시 누르면 선택이 해제됩니다."
            : "원티드에 반영할 근무코드를 선택하세요."}
        </p>
        {selectedShift ? (
          <div className="mt-3 flex w-full items-center gap-1.5 rounded-md bg-sky-50 py-1.5 pl-2 pr-1 text-xs text-text-main2">
            <span
              translate="no"
              className="flex size-4 shrink-0 items-center justify-center rounded text-[10px] font-bold"
              style={{
                backgroundColor: selectedShift.color,
                color: getReadableShiftCodeTextColor(selectedShift.color),
              }}
            >
              {selectedShift.shift_id}
            </span>
            <span className="truncate font-medium">{selectedShift.name}</span>
            <span className="shrink-0 text-text-sub/70">
              · {selectedShift.type?.trim() || "근무"}
            </span>
            <button
              type="button"
              aria-label={`${selectedShift.name} 근무코드 선택 해제`}
              title="선택 해제"
              className="ml-auto flex size-5 shrink-0 items-center justify-center rounded-sm text-text-sub transition-colors hover:bg-sky-100 hover:text-text-main2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sig"
              onClick={onClearSelection}
              disabled={disabled}
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          </div>
        ) : (
          <div
            className="mt-3 flex min-h-8 w-full items-center rounded-md bg-light-muted/70 px-2 text-xs text-text-sub"
            aria-live="polite"
          >
            선택 코드 없음
          </div>
        )}
      </div>
      <WantedShiftSearch
        shifts={shifts}
        selectedShiftId={selectedShiftId}
        disabled={disabled}
        onShiftChange={onShiftChange}
      />
    </aside>
  );
}
